import dayjs from "dayjs";
import api from "../../api/api";
import { practiceFeatures } from "../../config/practice";

/*
 * Front-desk payments as OSA (practice 7825) works them -- used only behind
 * practiceFeatures().osaPayments; 9928 keeps its own maths and routes.
 *
 * - Coinsurance is a DOLLAR amount (not a percent), stored by v1 as the
 *   visit's percentage = {"Amount": <dollars>}.
 * - The total is copay + self pay + balance + deductible + coinsurance.
 * - Update saves through v1 PUT /patient/{pid}/payments/update-patient-payments
 *   (OSA's route: no deductible-colour / order-cost maths), which exists on v1
 *   only, so it is pinned to v1 (`_gw`, see api/api.js).
 */

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Rounded to cents (raw float sums show 0.30000000000000004), as a string for
// the Total input.
export const osaPaymentTotal = ({ copay, selfPay, paymentOnAccount, deductible, coinsurance }) =>
  (Math.round((num(copay) + num(selfPay) + num(paymentOnAccount) + num(deductible) + num(coinsurance)) * 100) / 100).toString();

// The visit's coinsurance dollars (percentage.Amount; the column is JSON and
// may arrive as text).
export const osaCoinsuranceAmount = (row) => {
  let p = row?.percentage;
  if (typeof p === "string") {
    try {
      p = JSON.parse(p);
    } catch (e) {
      p = null;
    }
  }
  return num(p?.Amount);
};

// The visit date the payment routes key on ("YYYY-MM-DD"), or null when the
// card has none.
export const visitDateOf = (row) => {
  if (!row?.startdate) return null;
  const d = dayjs(row.startdate);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

// A card the payment routes can act on: it has an appointment and a date.
// A dashboard SEARCH for a patient with no visit on the chosen day returns a
// patient-only card (v1 appointments/dashboard fallback: startdate "",
// appointmentid ""). OSA's build sent those as searchdate "Invalid Date" and
// every such save failed with 400 (as did its view-payments read).
export const hasVisit = (row) => Boolean(row?.appointmentid) && visitDateOf(row) !== null;

export const putOsaPaymentUpdate = (practiseid, patientid, payload) =>
  api.put(`/v1/${practiseid}/patient/${patientid}/payments/update-patient-payments`, payload, { _gw: "v1" });

// The dashboard card's amount due, OSA's maths: what care already adds (cost
// estimate + self pay + balance + copay) plus the deductible and the
// coinsurance dollars. On a yellow (coinsurance) card the cost estimate IS
// percentage.Amount already, so it is not added twice (OSA's build did).
export const osaCardAmountDue = ({ base, row, coinsuranceInEstimate }) =>
  base + num(row?.deductibleamt) + (coinsuranceInEstimate ? 0 : osaCoinsuranceAmount(row));

// The amount due the dashboard card shows: care's total as is (9928), OSA's
// maths behind practiceFeatures().osaPayments (7825).
export const cardAmountDue = ({ careTotal, row, coinsuranceInEstimate }) =>
  practiceFeatures().osaPayments
    ? osaCardAmountDue({ base: careTotal, row, coinsuranceInEstimate })
    : careTotal;

// OSA batch 6 (scheduler-login). The Scheduler and sign-in sheet payment
// popups save through update-patient-payments too, which writes the visit's
// coinsurance dollars along with the other amounts. So a save is allowed only
// when the form holds THIS visit's view-payments row: a form that never loaded
// it (or still shows another visit's) would overwrite the visit with what is
// on screen.
export const isPaymentRowFor = (row, visit) =>
  Boolean(row && visit?.appointmentid) &&
  row.appointmentid != null &&
  String(row.appointmentid) === String(visit.appointmentid);

export const PAYMENT_ROW_NOT_LOADED =
  "This visit's payment details have not loaded. Close Payments and open it again.";
